import { useState, useEffect, useCallback } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MobileMenu } from "@/components/MobileMenu";

const links = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/contact", label: "Contact Us", testid: "nav-contact-link" },
  { to: "/events", label: "Events", testid: "nav-events-link" },
];

export default function Header() {
  const { content } = useSite();
  const { scrollY } = useScroll();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useMotionValueEvent(scrollY, "change", (v) => {
    setScrolled(v > 140);
  });

  const showLogo = !isHome || scrolled;

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b transition-colors duration-500 ${
        showLogo || scrolled
          ? "bg-navy/90 border-white/10"
          : "bg-navy/40 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 grid grid-cols-[1fr_auto_1fr] items-center">
        <div className="hidden sm:block">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-bold">
            Drywall · Acoustic · Lathing · Insulation
          </p>
        </div>
        <Link
          to="/"
          className={`justify-self-center transition-none ${showLogo ? "" : "pointer-events-none"}`}
          data-testid="header-logo-link"
          aria-hidden={!showLogo}
          tabIndex={showLogo ? 0 : -1}
        >
          <motion.img
            src={content.logo_url}
            alt="Drywall Local 675 — United Brotherhood of Carpenters and Joiners of America"
            data-testid="header-logo"
            initial={false}
            animate={{
              opacity: showLogo ? 1 : 0,
              scale: showLogo ? 1 : 0.7,
              y: showLogo ? 0 : -10,
            }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: showLogo ? 1.06 : 0.7 }}
            className="h-12 sm:h-14 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
          />
        </Link>
        <nav
          className="justify-self-end hidden sm:flex items-center gap-8"
          data-testid="main-nav"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-xs font-bold tracking-[0.18em] uppercase transition-colors duration-300 ${
                  isActive ? "text-brand-sky" : "text-white/75 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          data-testid="mobile-menu-button"
          className="justify-self-end sm:hidden grid place-items-center h-11 w-11 -mr-2 text-white/85 hover:text-white transition-colors duration-300"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>
      <MobileMenu
        open={menuOpen}
        onClose={closeMenu}
        links={links}
        contact={content.contact}
        logoUrl={content.logo_url}
      />
    </motion.header>
  );
}
